import React, { useState, useEffect, useRef } from 'react'
import ReactPlayer from 'react-player'
import { VideoPlayerMetaProps } from '../data/types'
import { Button, Card } from './ui'
import { PauseIcon, PlayIcon, VideoIcon, VolumeUpIcon, VolumeOffIcon, ExpandIcon, CompressIcon, CaptionsIcon } from '../icons'
import { LoadingSpinner } from './LoadingSpinner'

type Props = {
    videoUrl: string
    meta: VideoPlayerMetaProps
    onContentCompleted?: () => void
    captions?: Array<{ srclang: string; label: string; src: string }>
}

export const VideoPlayer = (props: Props) => {
    const { videoUrl, meta, onContentCompleted, captions = [] } = props
    const [error, setError] = useState<string | null>(null)
    const [ready, setReady] = useState(false)
    const [playing, setPlaying] = useState(false)
    const [progress, setProgress] = useState(0)
    const [duration, setDuration] = useState(0)
    const [hasTriggeredCompletion, setHasTriggeredCompletion] = useState(false)
    const [hasEnded, setHasEnded] = useState(false)
    const [volume, setVolume] = useState(1)
    const [muted, setMuted] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showVolumeSlider, setShowVolumeSlider] = useState(false)
    const [showFullscreenControls, setShowFullscreenControls] = useState(true)
    const [playbackRate, setPlaybackRate] = useState(1)
    const [showSpeedMenu, setShowSpeedMenu] = useState(false)
    const [captionsEnabled, setCaptionsEnabled] = useState(false)
    const [showCaptionsMenu, setShowCaptionsMenu] = useState(false)
    const [selectedCaptionIndex, setSelectedCaptionIndex] = useState<number | null>(null)
    const videoElementRef = useRef<HTMLVideoElement | null>(null)
    const playerContainerRef = useRef<HTMLDivElement>(null)
    const fullscreenControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const speedOptions = [0.5, 1, 1.5, 2]
    const isYoutubeVideo = ReactPlayer.canPlay(videoUrl) && videoUrl.includes('youtube.com')

    // Fallback timeout to hide loading spinner if onReady doesn't fire
    useEffect(() => {
        const timer = setTimeout(() => {
            if (!ready && !error) {
                setReady(videoElementRef.current?.readyState > 0)
            }
        }, 3000) // Hide loading after 3 seconds max

        return () => clearTimeout(timer)
    }, [ready, error])

    // Control caption track visibility via textTracks API
    useEffect(() => {
        if (!ready || captions.length === 0) return

        const videoElement = videoElementRef.current

        if (!videoElement?.textTracks) return

        const tracks = videoElement.textTracks

        // Disable all tracks first
        for (let i = 0; i < tracks.length; i++) {
            tracks[i].mode = 'disabled'
        }

        // Enable selected track if captions are enabled
        if (captionsEnabled && selectedCaptionIndex !== null && selectedCaptionIndex < tracks.length) {
            console.log(`Enabling track ${selectedCaptionIndex}`)
            tracks[selectedCaptionIndex].mode = 'showing'
        }
    }, [ready, captionsEnabled, selectedCaptionIndex, captions.length])

    // Monitor fullscreen state changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
        }

        document.addEventListener('fullscreenchange', handleFullscreenChange)
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }, [])

    // Handle mouse/touch movement in fullscreen to show/hide controls
    useEffect(() => {
        if (!isFullscreen) return

        const handleUserActivity = () => {
            // Don't hide controls if volume slider is open
            if (showVolumeSlider) {
                setShowFullscreenControls(true)
                return
            }

            setShowFullscreenControls(true)

            if (fullscreenControlsTimeoutRef.current) {
                clearTimeout(fullscreenControlsTimeoutRef.current)
            }

            fullscreenControlsTimeoutRef.current = setTimeout(() => {
                if (!showVolumeSlider) {
                    setShowFullscreenControls(false)
                }
            }, 3000)
        }

        const container = playerContainerRef.current
        if (!container) return

        container.addEventListener('mousemove', handleUserActivity)
        container.addEventListener('touchstart', handleUserActivity)
        container.addEventListener('touchmove', handleUserActivity)

        return () => {
            container.removeEventListener('mousemove', handleUserActivity)
            container.removeEventListener('touchstart', handleUserActivity)
            container.removeEventListener('touchmove', handleUserActivity)
            if (fullscreenControlsTimeoutRef.current) {
                clearTimeout(fullscreenControlsTimeoutRef.current)
            }
        }
    }, [isFullscreen, showVolumeSlider])

    const togglePlaying = () => {
        setPlaying((prev) => {
            if (!prev && hasEnded) {
                // Reset to beginning if video has ended
                setHasEnded(false)
                if (videoElementRef.current) {
                    videoElementRef.current.currentTime = 0
                }
            }
            return !prev
        })
    }

    const toggleVolumeSlider = () => {
        setShowVolumeSlider((prev) => !prev)
    }

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVolume = parseFloat(e.target.value)
        setVolume(newVolume)
        if (newVolume > 0 && muted) {
            setMuted(false)
        }
    }

    const handleSpeedChange = (speed: number) => {
        setPlaybackRate(speed)
        setShowSpeedMenu(false)
    }

    const toggleCaptionsMenu = () => {
        setShowCaptionsMenu((prev) => !prev)
    }

    const handleCaptionSelect = (index: number) => {
        // Enable and select caption
        setCaptionsEnabled(true)
        setSelectedCaptionIndex(index)
        setShowCaptionsMenu(false)
    }

    const toggleFullscreen = async () => {
        if (!playerContainerRef.current) return

        try {
            if (!isFullscreen) {
                await playerContainerRef.current.requestFullscreen()
            } else {
                await document.exitFullscreen()
            }
        } catch (err) {
            console.error('Fullscreen error:', err)
        }
    }

    const formatTime = (seconds: number) => {
        if (!seconds || !isFinite(seconds)) return '0:00'
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const getPercentage = () => {
        if (!duration || duration === 0) return 0
        return (progress / duration) * 100
    }

    // Check if URL is valid
    if (!videoUrl) {
        return (
            <Card>
                <div className="p-8 text-center">
                    <VideoIcon className="w-12 h-12 mx-auto mb-4 text-danger-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {meta.errorTitle}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">{meta.errorInvalidUrl}</p>
                </div>
            </Card>
        )
    }

    if (error) {
        return (
            <Card>
                <div className="p-8 text-center">
                    <VideoIcon className="w-12 h-12 mx-auto mb-4 text-danger-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {meta.errorTitle}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">{error}</p>
                </div>
            </Card>
        )
    }

    return (
        <>
            <Card>
                <div
                    ref={playerContainerRef}
                    className="relative bg-gray-200 dark:bg-gray-800 aspect-video mx-auto max-h-[70vh]"
                >
                    {!ready && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-800 z-10">
                            <LoadingSpinner meta={{ loadingText: meta.loadingText }} />
                        </div>
                    )}
                    <ReactPlayer
                        ref={videoElementRef}
                        src={videoUrl}
                        key={videoUrl}
                        className="absolute top-0 left-0"
                        width="100%"
                        height="100%"
                        playing={playing && !hasEnded}
                        volume={volume}
                        muted={muted}
                        controls={false}
                        loop={false}
                        playbackRate={playbackRate}
                        crossOrigin="anonymous"
                        config={{
                            youtube: {
                                origin: window.location.origin,
                                enablejsapi: 1,
                                color: 'white',
                                disablekb: 1
                            }
                        }}
                        onReady={() => setReady(true)}
                        onStart={() => setReady(true)}
                        onPlay={() => {
                            setReady(true)
                            setPlaying(true)
                        }}
                        onPause={() => setPlaying(false)}
                        onEnded={() => {
                            setPlaying(false)
                            setHasEnded(true)
                            setProgress(duration)
                            if (!hasTriggeredCompletion && onContentCompleted) {
                                setHasTriggeredCompletion(true)
                                onContentCompleted()
                            }
                        }}
                        onError={(e) => {
                            console.error('Video player error:', e)
                            setError(meta.errorLoadingVideo)
                        }}
                        onDurationChange={(e) => setDuration(e.currentTarget.duration)}
                        onTimeUpdate={(e) => {
                            const currentTime = e.currentTarget.currentTime
                            setProgress(currentTime)
                            // Trigger completion when video is about to end (1 second threshold)
                            if (!hasTriggeredCompletion && onContentCompleted && duration > 0) {
                                if ((duration - currentTime) <= 1) {
                                    setHasTriggeredCompletion(true)
                                    onContentCompleted()
                                }
                            }
                        }}
                    >
                        {captions.map((caption, index) => (
                            <track
                                key={index}
                                kind="subtitles"
                                src={caption.src}
                                srcLang={caption.srclang}
                                label={caption.label}
                            />
                        ))}
                    </ReactPlayer>

                    {/* Fullscreen Overlay Controls */}
                    {isFullscreen && ready && (
                        <div
                            className={`absolute inset-0 bg-gradient-to-t from-black/70 via-transparent transition-opacity duration-300 ${
                                showFullscreenControls ? 'opacity-100' : 'opacity-0'
                            }`}
                            style={{ pointerEvents: showFullscreenControls ? 'auto' : 'none' }}
                        >
                            {/* Bottom controls bar */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col gap-4">
                                {/* Progress Bar */}
                                <div className="relative">
                                    <div className="h-1.5 bg-white/30 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-primary-500 transition-all duration-300 rounded-full"
                                            style={{ width: `${getPercentage()}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Controls Row */}
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={togglePlaying}
                                        className='w-12 h-12 flex items-center justify-center text-white hover:text-primary-400 transition-colors rounded-full hover:bg-white/10'
                                        aria-label={playing ? 'Pause' : 'Play'}
                                    >
                                        {!playing ? (
                                            <PlayIcon className="w-7 h-7" />
                                        ) : (
                                            <PauseIcon className="w-7 h-7" />
                                        )}
                                    </button>

                                    <div className="flex-1 flex items-center gap-3 text-white text-base">
                                        <span className="font-mono font-medium">{formatTime(progress)}</span>
                                        <span className="text-white/60">/</span>
                                        <span className="font-mono">{formatTime(duration)}</span>
                                    </div>

                                    <div className="relative flex flex-col items-center gap-3">
                                        {showVolumeSlider && (
                                            <div className="absolute bottom-full mb-2 p-2 bg-black/50 rounded-lg">
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.05"
                                                    value={muted ? 0 : volume}
                                                    onChange={handleVolumeChange}
                                                    onBlur={() => setShowVolumeSlider(false)}
                                                    className="h-24 accent-primary-500 cursor-pointer block"
                                                    aria-label="Volume"
                                                    autoFocus
                                                    style={{
                                                        writingMode: 'vertical-lr',
                                                        direction: 'rtl',
                                                        width: '6px'
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <button
                                            onClick={toggleVolumeSlider}
                                            className='w-10 h-10 flex items-center justify-center text-white hover:text-primary-400 transition-colors rounded'
                                            aria-label={muted ? meta.unmuteLabel : meta.muteLabel}
                                        >
                                            {muted || volume === 0 ? (
                                                <VolumeOffIcon className="w-6 h-6" />
                                            ) : (
                                                <VolumeUpIcon className="w-6 h-6" />
                                            )}
                                        </button>
                                    </div>

                                    {!isYoutubeVideo && (<div className="relative">
                                        {showSpeedMenu && (
                                            <div className="absolute bottom-full mb-2 bg-black/90 rounded-lg overflow-hidden min-w-[100px]">
                                                {speedOptions.map((speed) => (
                                                    <button
                                                        key={speed}
                                                        onClick={() => handleSpeedChange(speed)}
                                                        className={`w-full px-4 py-2.5 text-start text-white hover:bg-primary-500/30 transition-colors ${
                                                            playbackRate === speed ? 'bg-primary-500/50 font-semibold' : ''
                                                        }`}
                                                    >
                                                        {speed}×
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                                            onBlur={() => setTimeout(() => setShowSpeedMenu(false), 200)}
                                            className='h-10 px-2.5 flex items-center justify-center text-white hover:text-primary-400 transition-colors rounded font-mono text-lg'
                                            style={{fontWeight: 'bold'}}
                                            aria-label={meta.playbackSpeedLabel}
                                        >
                                            {playbackRate}×
                                        </button>
                                    </div>)}

                                    {/* Captions Toggle */}
                                    {captions.length > 0 && (
                                        <div className="relative">
                                            {showCaptionsMenu && (
                                                <div className="absolute bottom-full mb-2 bg-black/90 rounded-lg overflow-hidden min-w-[120px]">
                                                    <button
                                                        onClick={() => {
                                                            setCaptionsEnabled(false)
                                                            setSelectedCaptionIndex(null)
                                                            setShowCaptionsMenu(false)
                                                        }}
                                                        className={`w-full px-4 py-2.5 text-start text-white hover:bg-primary-500/30 transition-colors ${
                                                            !captionsEnabled ? 'bg-primary-500/50 font-semibold' : ''
                                                        }`}
                                                    >
                                                        Off
                                                    </button>
                                                    {captions.map((caption, index) => (
                                                        <button
                                                            key={index}
                                                            onClick={() => handleCaptionSelect(index)}
                                                            className={`w-full px-4 py-2.5 text-start text-white hover:bg-primary-500/30 transition-colors ${
                                                                captionsEnabled && selectedCaptionIndex === index ? 'bg-primary-500/50 font-semibold' : ''
                                                            }`}
                                                        >
                                                            {caption.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            <button
                                                onClick={toggleCaptionsMenu}
                                                onBlur={() => setTimeout(() => setShowCaptionsMenu(false), 200)}
                                                className={`w-10 h-10 flex items-center justify-center transition-colors rounded text-white hover:text-primary-400`}
                                                aria-label={meta.captionsLabel || 'Captions'}
                                            >
                                                <CaptionsIcon className="w-6 h-6" enabled={captionsEnabled} />
                                            </button>
                                        </div>
                                    )}

                                    <button
                                        onClick={toggleFullscreen}
                                        className='w-10 h-10 flex items-center justify-center text-white hover:text-primary-400 transition-colors rounded'
                                        aria-label={meta.exitFullscreenLabel}
                                    >
                                        <CompressIcon className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            {/* Custom controls and progress bar */}
            {ready && (
                <Card className="mt-4 overflow-visible">
                    <div className="p-4 flex flex-col gap-3">
                        {/* Play/Pause Button Row */}
                        <div className="flex items-center gap-3">
                            <Button
                                variant='solid'
                                color='primary'
                                size='sm'
                                onClick={togglePlaying}
                                className='w-10 h-10 !p-0 flex items-center justify-center'
                                aria-label={playing ? 'Pause' : 'Play'}
                            >
                                {!playing ? (
                                    <PlayIcon className="w-5 h-5" />
                                ) : (
                                    <PauseIcon className="w-5 h-5" />
                                )}
                            </Button>

                            <div className="flex-1 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-mono font-medium">{formatTime(progress)}</span>
                                <span className="text-gray-400 dark:text-gray-600">/</span>
                                <span className="font-mono">{formatTime(duration)}</span>
                                <span className="mx-2 text-gray-400 dark:text-gray-600 hidden sm:inline">•</span>
                                <span className="font-semibold text-primary-600 dark:text-primary-400 hidden sm:inline">
                                    {Math.round(getPercentage())}%
                                </span>
                            </div>

                            {/* Volume Control */}
                            <div className="relative flex flex-col sm:flex-row items-center gap-2">
                                {showVolumeSlider && (
                                    <div className="absolute bottom-full mb-2 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg sm:hidden">
                                        <input
                                            type="range"
                                            min="0"
                                            max="1"
                                            step="0.05"
                                            value={muted ? 0 : volume}
                                            onChange={handleVolumeChange}
                                            onBlur={() => setShowVolumeSlider(false)}
                                            className="h-24 accent-primary-500 cursor-pointer block"
                                            aria-label="Volume"
                                            autoFocus
                                            style={{
                                                writingMode: 'vertical-lr',
                                                direction: 'rtl',
                                                width: '6px'
                                            }}
                                        />
                                    </div>
                                )}
                                <Button
                                    variant='icon'
                                    onClick={toggleVolumeSlider}
                                    aria-label={muted ? meta.unmuteLabel : meta.muteLabel}
                                >
                                    {muted || volume === 0 ? (
                                        <VolumeOffIcon />
                                    ) : (
                                        <VolumeUpIcon />
                                    )}
                                </Button>
                                {showVolumeSlider && (
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={muted ? 0 : volume}
                                        onChange={handleVolumeChange}
                                        onBlur={() => setShowVolumeSlider(false)}
                                        className="hidden sm:block w-24 accent-primary-500 cursor-pointer"
                                        aria-label="Volume"
                                        autoFocus
                                    />
                                )}
                            </div>

                            {/* Playback Speed Control */}
                            {!isYoutubeVideo && (<div className="relative">
                                {showSpeedMenu && (
                                    <div className="absolute bottom-full mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-w-[90px]">
                                        {speedOptions.map((speed) => (
                                            <button
                                                key={speed}
                                                onClick={() => handleSpeedChange(speed)}
                                                className={`w-full px-3 py-2 text-start text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors ${
                                                    playbackRate === speed
                                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold'
                                                        : 'text-gray-700 dark:text-gray-300'
                                                }`}
                                            >
                                                {speed}×
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <Button
                                    variant='icon'
                                    size='sm'
                                    onClick={() => setShowSpeedMenu(!showSpeedMenu)}
                                    onBlur={() => setTimeout(() => setShowSpeedMenu(false), 200)}
                                    aria-label={meta.playbackSpeedLabel}
                                    className='!px-1 h-9 !text-lg font-mono'
                                    style={{fontWeight: 'bold'}}
                                >
                                    {playbackRate}×
                                </Button>
                            </div>)}

                            {/* Captions Toggle */}
                            {captions.length > 0 && (
                                <div className="relative">
                                    {showCaptionsMenu && (
                                        <div className="absolute bottom-full mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-w-[120px]">
                                            <button
                                                onClick={() => {
                                                    setCaptionsEnabled(false)
                                                    setSelectedCaptionIndex(null)
                                                    setShowCaptionsMenu(false)
                                                }}
                                                className={`w-full px-3 py-2 text-start text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors ${
                                                    !captionsEnabled
                                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold'
                                                        : 'text-gray-700 dark:text-gray-300'
                                                }`}
                                            >
                                                Off
                                            </button>
                                            {captions.map((caption, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => handleCaptionSelect(index)}
                                                    className={`w-full px-3 py-2 text-start text-sm hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors ${
                                                        captionsEnabled && selectedCaptionIndex === index
                                                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-semibold'
                                                            : 'text-gray-700 dark:text-gray-300'
                                                    }`}
                                                >
                                                    {caption.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <Button
                                        variant='icon'
                                        onClick={toggleCaptionsMenu}
                                        onBlur={() => setTimeout(() => setShowCaptionsMenu(false), 200)}
                                        aria-label={meta.captionsLabel || 'Captions'}
                                    >
                                        <CaptionsIcon enabled={captionsEnabled} />
                                    </Button>
                                </div>
                            )}

                            {/* Fullscreen Button */}
                            <Button
                                variant='icon'
                                onClick={toggleFullscreen}
                                aria-label={isFullscreen ? meta.exitFullscreenLabel : meta.enterFullscreenLabel}
                            >
                                {isFullscreen ? (
                                    <CompressIcon />
                                ) : (
                                    <ExpandIcon />
                                )}
                            </Button>
                        </div>

                        {/* Progress Bar */}
                        <div className="relative">
                            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner">
                                <div
                                    className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300 rounded-full shadow-sm"
                                    style={{ width: `${getPercentage()}%` }}
                                />
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </>
    )
}
